<template>
  <div class="acsc-cascade bg-white border border-gray-200 rounded-lg" data-private>
    <h3 class="section-title">ACSC Cascade — Avoidable ED Visits</h3>
    <!-- Summary cards -->
    <div class="summary-cards" v-if="summary">
      <div class="stat-card">
        <div class="stat-value">{{ formatNumber(summary.totalEncounters) }}</div>
        <div class="stat-label">ACSC Encounters</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ formatNumber(summary.admittedCount) }}</div>
        <div class="stat-label">Admitted ({{ formatPercent(summary.admitRate) }})</div>
      </div>
      <div class="stat-card highlight">
        <div class="stat-value">{{ formatPercent(summary.edConversionRate) }}</div>
        <div class="stat-label">ED → Admission Rate</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ formatPercent(summary.snfRate) }}</div>
        <div class="stat-label">IP → SNF Rate</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ formatCurrency(summary.avgCost) }}</div>
        <div class="stat-label">Avg Admission Cost</div>
      </div>
      <div class="stat-card">
        <div class="stat-value">{{ formatCurrency(summary.totalCost) }}</div>
        <div class="stat-label">Total Cascade Cost</div>
      </div>
    </div>

    <!-- Condition filter chips. Click the label to filter the cascade; click
         the → to drill straight into that condition's patient list. -->
    <div class="condition-filters" v-if="conditions.length">
      <span
        v-for="c in conditions"
        :key="c"
        :class="['chip chip-condition', { active: selectedConditions.includes(c) }]"
      >
        <span class="chip-label" @click="toggleCondition(c)">{{ c }} ({{ conditionCounts[c] || 0 }})</span>
        <span class="chip-drill" title="View these patients" @click.stop="drillCondition(c)">→</span>
      </span>
      <button class="chip reset" @click="selectedConditions = [...conditions]" v-if="selectedConditions.length < conditions.length">
        Show All
      </button>
    </div>

    <!-- Sankey diagram -->
    <div class="sankey-wrapper" v-if="chartData">
      <SankeyDiagram
        :chartData="chartData"
        :config="sankeyConfig"
        @node-click="onNodeClick"
      />
    </div>

    <div class="loading" v-else-if="loading">Loading cascade data...</div>
    <div class="empty" v-else-if="!loading && !episodes.length">No ACSC episodes found.</div>

    <!-- Drilldown patient table -->
    <div v-if="drilldownFilter" class="drilldown-section">
      <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:8px;">
        <h4 class="drilldown-title">{{ drilldownFilter.label }} <span class="drilldown-count">({{ drilldownRows.length }})</span></h4>
        <div style="display:flex; gap:6px;">
          <ExportMenu :disabled="!drilldownRows.length" :exports="[{ type: 'csv', action: exportDrilldown }]" />
          <button class="chip reset" @click="drilldownFilter = null" style="font-size:11px;">✕ Close</button>
        </div>
      </div>
      <div class="drilldown-table-wrap">
        <table class="drilldown-table">
          <thead>
            <tr>
              <th @click="sortDrilldown('bene_last_name')" class="sortable">Patient</th>
              <th @click="sortDrilldown('acsc_condition')" class="sortable">Condition</th>
              <th @click="sortDrilldown('primary_dgns_cd')" class="sortable">DX</th>
              <th @click="sortDrilldown('facility_name')" class="sortable">Facility</th>
              <th @click="sortDrilldown('had_ip')" class="sortable">Admitted</th>
              <th>Disposition</th>
              <th @click="sortDrilldown('ip_admit_dt')" class="sortable">Admit</th>
              <th @click="sortDrilldown('ip_discharge_dt')" class="sortable">Discharge</th>
              <th @click="sortDrilldown('ip_los')" class="sortable num">IP Days</th>
              <th @click="sortDrilldown('snf_days')" class="sortable num">SNF Days</th>
              <th @click="sortDrilldown('ip_cost')" class="sortable num">IP Cost</th>
              <th @click="sortDrilldown('ed_cost')" class="sortable num">ED Cost</th>
              <th @click="sortDrilldown('snf_cost')" class="sortable num">SNF Cost</th>
              <th @click="sortDrilldown('total_cascade_cost')" class="sortable num">Total Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(ep, i) in drilldownRows" :key="i">
              <td class="sensitive">{{ ep.bene_last_name }}, {{ ep.bene_first_name }}</td>
              <td>{{ ep.acsc_condition }}</td>
              <td>{{ ep.primary_dgns_cd }}</td>
              <td class="sensitive">{{ ep.facility_name }}</td>
              <td>{{ ep.had_ip ? 'Yes' : 'No' }}</td>
              <td>{{ disposition(ep) }}</td>
              <td>{{ ep.ip_admit_dt ? ep.ip_admit_dt.slice(0, 10) : '—' }}</td>
              <td>{{ ep.ip_discharge_dt ? ep.ip_discharge_dt.slice(0, 10) : '—' }}</td>
              <td class="num">{{ ep.ip_los ?? '—' }}</td>
              <td class="num">{{ ep.snf_days ?? '—' }}</td>
              <td class="num">{{ ep.ip_cost != null ? formatCurrency(ep.ip_cost) : '—' }}</td>
              <td class="num">{{ ep.ed_cost != null ? formatCurrency(ep.ed_cost) : '—' }}</td>
              <td class="num">{{ ep.snf_cost != null ? formatCurrency(ep.snf_cost) : '—' }}</td>
              <td class="num">{{ ep.total_cascade_cost != null ? formatCurrency(ep.total_cascade_cost) : '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
// Uses this.$axios for auth headers and base URL
import SankeyDiagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'
import { beginSensitiveEgress, downloadSensitiveBlob } from '@/utils/demoEgress'

export default {
  name: 'ACSCCascadeSankey',
  components: { SankeyDiagram, ExportMenu },
  props: {
    metricId: { type: Number, default: null },
    acscMetricId: { type: Number, default: 644 },
    entityId: { type: Number, default: null },
    year: { type: Number, default: null },
  },
  data() {
    return {
      episodes: [],
      loading: false,
      selectedConditions: [],
      worklistId: null,
      drilldownFilter: null,  // { label, fn } — set by node click
      drilldownSort: 'total_cascade_cost',
      drilldownSortDir: 'desc',
    }
  },
  computed: {
    filteredEpisodes() {
      if (!this.selectedConditions.length) return this.episodes
      return this.episodes.filter(e => this.selectedConditions.includes(e.acsc_condition))
    },
    conditions() {
      const counts = {}
      this.episodes.forEach(e => {
        if (e.acsc_condition) counts[e.acsc_condition] = (counts[e.acsc_condition] || 0) + 1
      })
      return Object.keys(counts).sort((a, b) => counts[b] - counts[a])
    },
    conditionCounts() {
      const counts = {}
      this.episodes.forEach(e => {
        if (e.acsc_condition) counts[e.acsc_condition] = (counts[e.acsc_condition] || 0) + 1
      })
      return counts
    },
    summary() {
      const eps = this.filteredEpisodes
      if (!eps.length) return null
      const admitted = eps.filter(e => e.had_ip)
      const edVisits = eps.filter(e => e.had_ed)
      const edAdmitted = eps.filter(e => e.had_ed && e.had_ip)
      const snfCount = admitted.filter(e => e.had_snf).length
      const totalCost = admitted.reduce((s, e) => s + (e.total_cascade_cost || 0), 0)
      return {
        totalEncounters: eps.length,
        admittedCount: admitted.length,
        admitRate: admitted.length / eps.length,
        edConversionRate: edVisits.length ? edAdmitted.length / edVisits.length : 0,
        snfRate: admitted.length ? snfCount / admitted.length : 0,
        avgCost: admitted.length ? totalCost / admitted.length : 0,
        totalCost,
      }
    },
    chartData() {
      const eps = this.filteredEpisodes
      if (!eps.length) return null

      // Full cascade:
      // Stage 0: ACSC Encounter (all episodes)
      // Stage 1: ED Visit vs Direct IP Admit
      // Stage 2: ED → Admitted vs ED → Sent Home, Direct → IP
      // Stage 3: IP → SNF vs IP → Discharged

      const total = eps.length
      const edVisits = eps.filter(e => e.had_ed).length
      const directIP = eps.filter(e => !e.had_ed && e.had_ip).length
      const edToIP = eps.filter(e => e.had_ed && e.had_ip).length
      const edHome = eps.filter(e => e.had_ed && !e.had_ip).length
      const totalIP = edToIP + directIP
      const ipToSNF = eps.filter(e => e.had_ip && e.had_snf).length
      const ipDischarged = totalIP - ipToSNF

      const nodes = [
        { name: `ACSC Encounter\n${this.formatNumber(total)}`, color: '#6366f1' },
        { name: `ED Visit\n${this.formatNumber(edVisits)}`, color: '#ef4444' },
        { name: `Direct Admit\n${this.formatNumber(directIP)}`, color: '#f97316' },
        { name: `Admitted\n${this.formatNumber(totalIP)}`, color: '#3b82f6' },
        { name: `Sent Home\n${this.formatNumber(edHome)}`, color: '#22c55e' },
        { name: `SNF\n${this.formatNumber(ipToSNF)}`, color: '#8b5cf6' },
        { name: `Discharged\n${this.formatNumber(ipDischarged)}`, color: '#10b981' },
      ]

      const links = [
        { source: 0, target: 1, value: edVisits },      // Encounter → ED
        { source: 0, target: 2, value: directIP },       // Encounter → Direct Admit
        { source: 1, target: 3, value: edToIP },          // ED → Admitted
        { source: 1, target: 4, value: edHome },           // ED → Sent Home
        { source: 2, target: 3, value: directIP },        // Direct → Admitted
        { source: 3, target: 5, value: ipToSNF },         // Admitted → SNF
        { source: 3, target: 6, value: ipDischarged },    // Admitted → Discharged
      ].filter(l => l.value > 0)

      const stageLabels = [
        { label: 'Encounter', position: 0 },
        { label: 'Pathway', position: 0.25 },
        { label: 'Outcome', position: 0.6 },
        { label: 'Post-Acute', position: 0.9 },
      ]

      return { nodes, links, stageLabels }
    },
    drilldownRows() {
      if (!this.drilldownFilter) return []
      let rows = this.filteredEpisodes.filter(this.drilldownFilter.fn)
      const key = this.drilldownSort
      const dir = this.drilldownSortDir === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => {
        const va = a[key] ?? '', vb = b[key] ?? ''
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        return String(va).localeCompare(String(vb)) * dir
      })
      return rows
    },
    sankeyConfig() {
      return {
        width: 900,
        height: 450,
        margin: { top: 40, right: 150, bottom: 20, left: 10 },
        nodeWidth: 18,
        nodePadding: 50,
        iterations: 32,
        linkOpacity: 0.5,
        labelFontSize: 11,
      }
    },
  },
  watch: {
    metricId: { handler() { this.fetchData() }, immediate: false },
    entityId: { handler() { this.fetchData() } },
    year: { handler() { this.fetchData() } },
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        const resp = await this.$axios.post('/api/impact_analytics/patient-worklists/by-metric/', {
          metric_id: this.acscMetricId,
          selected_columns: [
            'bene_mbi_id', 'bene_first_name', 'bene_last_name',
            'acsc_condition', 'primary_dgns_cd', 'facility_name', 'bene_pcp',
            'had_ed', 'had_ip', 'had_snf',
            'ip_admit_dt', 'ip_discharge_dt', 'ip_los',
            'ip_cost', 'ed_cost', 'snf_cost', 'snf_days', 'total_cascade_cost',
          ],
          entity_id: this.entityId || undefined,
          observation_year: this.year || 2025,
          start_row: 0,
          end_row: 50000,
        })

        this.episodes = resp.data?.rowData || []
        this.selectedConditions = [...this.conditions]
      } catch (e) {
        console.error('ACSC cascade fetch error:', e)
        this.episodes = []
      } finally {
        this.loading = false
      }
    },
    toggleCondition(c) {
      const idx = this.selectedConditions.indexOf(c)
      if (idx >= 0) {
        this.selectedConditions.splice(idx, 1)
      } else {
        this.selectedConditions.push(c)
      }
    },
    drillCondition(c) {
      // Drill straight into one condition's patient list (Jennifer's
      // "click Heart Failure → see those patients"). Also isolate the
      // cascade to that condition so the diagram matches the table.
      this.selectedConditions = [c]
      this.drilldownFilter = { label: `${c} — ACSC Encounters`, fn: e => e.acsc_condition === c }
    },
    onNodeClick(data) {
      const name = (data.name || '').split('\n')[0].trim()
      const filters = {
        'ACSC Encounter': { label: 'All ACSC Encounters', fn: () => true },
        'ED Visit':       { label: 'ED Visits', fn: e => e.had_ed },
        'Direct Admit':   { label: 'Direct Admissions (no ED)', fn: e => !e.had_ed && e.had_ip },
        'Admitted':        { label: 'All Admissions', fn: e => e.had_ip },
        'Sent Home':       { label: 'ED — Sent Home', fn: e => e.had_ed && !e.had_ip },
        'SNF':             { label: 'IP → SNF', fn: e => e.had_ip && e.had_snf },
        'Discharged':      { label: 'IP → Discharged (no SNF)', fn: e => e.had_ip && !e.had_snf },
      }
      this.drilldownFilter = filters[name] || { label: name, fn: () => true }
    },
    exportDrilldown() {
      // Rows carry beneficiary names and facility names.
      const ticket = beginSensitiveEgress('export')
      if (!ticket) return
      const headers = ['Patient', 'Condition', 'DX', 'Facility', 'Admitted', 'Disposition',
        'Admit', 'Discharge', 'IP Days', 'SNF Days', 'IP Cost', 'ED Cost', 'SNF Cost', 'Total Cost']
      const rows = this.drilldownRows.map(e => [
        `${e.bene_last_name}, ${e.bene_first_name}`,
        e.acsc_condition, e.primary_dgns_cd, e.facility_name,
        e.had_ip ? 'Yes' : 'No', this.disposition(e),
        e.ip_admit_dt ? e.ip_admit_dt.slice(0, 10) : '',
        e.ip_discharge_dt ? e.ip_discharge_dt.slice(0, 10) : '',
        e.ip_los ?? '', e.snf_days ?? '',
        e.ip_cost ?? '', e.ed_cost ?? '', e.snf_cost ?? '', e.total_cascade_cost ?? '',
      ])
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      downloadSensitiveBlob(
        blob,
        `acsc_${this.drilldownFilter.label.replace(/\s+/g, '_').toLowerCase()}.csv`,
        ticket
      )
    },
    sortDrilldown(key) {
      if (this.drilldownSort === key) {
        this.drilldownSortDir = this.drilldownSortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.drilldownSort = key
        this.drilldownSortDir = 'desc'
      }
    },
    disposition(ep) {
      // Where the ACSC encounter ended up — derived from the cascade flags.
      if (ep.had_ip && ep.had_snf) return 'Admitted → SNF'
      if (ep.had_ip) return 'Admitted → Discharged'
      if (ep.had_ed) return 'ED only (sent home)'
      return 'Observation / other'
    },
    formatNumber(n) {
      if (n == null) return '—'
      return n.toLocaleString()
    },
    formatPercent(n) {
      if (n == null) return '—'
      return (n * 100).toFixed(1) + '%'
    },
    formatCurrency(n) {
      if (n == null) return '—'
      return '$' + Math.round(n).toLocaleString()
    },
  },
}
</script>

<style scoped>
.acsc-cascade {
  padding: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 12px 0;
}

.summary-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.stat-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  min-width: 120px;
  flex: 1;
}

.stat-card.highlight {
  background: #fef3c7;
  border-color: #fbbf24;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.condition-filters {
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.chip {
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid #cbd5e1;
  background: #fff;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.chip.active {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
}

.chip.reset {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #475569;
}

.chip:hover {
  opacity: 0.85;
}

/* Condition chip = filter label + drill arrow. */
.chip-condition {
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 0;
  overflow: hidden;
}
.chip-condition .chip-label {
  padding: 4px 8px 4px 12px;
  cursor: pointer;
}
.chip-condition .chip-drill {
  padding: 4px 10px;
  cursor: pointer;
  font-weight: 700;
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  opacity: 0.75;
}
.chip-condition.active .chip-drill {
  border-left-color: rgba(255, 255, 255, 0.4);
}
.chip-condition .chip-drill:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.06);
}

.sankey-wrapper {
  position: relative;
  height: 470px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.loading, .empty {
  text-align: center;
  padding: 40px;
  color: #94a3b8;
  font-size: 14px;
}

.drilldown-section {
  margin-top: 16px;
  border-top: 1px solid #e2e8f0;
  padding-top: 12px;
}

.drilldown-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.drilldown-count {
  font-weight: 400;
  color: #94a3b8;
}

.drilldown-table-wrap {
  max-height: 400px;
  overflow: auto;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.drilldown-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.drilldown-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 10px;
  text-align: left;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.drilldown-table th.sortable {
  cursor: pointer;
}

.drilldown-table th.sortable:hover {
  color: #1e293b;
}

.drilldown-table th.num,
.drilldown-table td.num {
  text-align: right;
}

.drilldown-table td {
  padding: 5px 10px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  white-space: nowrap;
}

.drilldown-table tbody tr:hover {
  background: #f8fafc;
}
</style>
